import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import {
  Events,
  Medication,
  MedicationRepositoryService,
  WebSocketService
} from '@xmedical/core';
import { MedicationSearchEntry, medicationSearchEntries } from '@xmedical/shared';
import { PatientStore } from './patient.store';
/**
 * PatientMedicationStore — current medication for the patient.
 *
 * Stores the full medication list (not split by scriptType — consumers can
 * compute filtered slices). Auto-loads on patient change, owns the
 * MedicationMedicationListChanged push subscription, exposes optimistic
 * add/remove with rollback. Server returns a new id from add().
 */
@Injectable({
  providedIn: 'root'
})
export class PatientMedicationStore {
  private patientStore = inject(PatientStore);
  private repo = inject(MedicationRepositoryService);
  private websocket = inject(WebSocketService);

  private readonly _items = signal<Medication[]>([]);
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);

  readonly items = this._items.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly count = computed(() => this._items().length);

  private currentChannel: string | null = null;
  private readonly eventName = Events.MedicationMedicationListChanged;
  // Cancels any in-flight `fetch()` HTTP when a newer request arrives.
  private fetchSub?: Subscription;

  constructor() {
    effect(() => {
      const patient = this.patientStore.current();
      const channel = this.patientStore.patientEventsChannelName();

      if (this.currentChannel && this.currentChannel !== channel) {
        this.websocket.unsubscribe(this.currentChannel, this.eventName);
        this.currentChannel = null;
      }

      if (!patient) {
        this.fetchSub?.unsubscribe();
        this._loading.set(false);
        this._items.set([]);
        this._error.set(null);
        return;
      }

      this.fetch(patient.id);

      if (channel && this.currentChannel !== channel) {
        this.websocket.subscribe(channel, this.eventName, () => this.reload());
        this.currentChannel = channel;
      }
    });
  }

  reload(): void {
    const patient = this.patientStore.current();
    if (!patient) return;
    this.fetch(patient.id);
  }

  add(medication: Medication): Observable<unknown> {
    const patient = this.patientStore.current();
    if (!patient) {
      throw new Error('PatientMedicationStore.add() called with no current patient');
    }
    const previous = this._items();
    this._items.set([...previous, medication]);
    return this.repo.addMedicationToPatient(patient.id, medication).pipe(
      tap({
        next: (response: any) => {
          const newId = typeof response === 'number' ? response : response?.value;
          if (typeof newId === 'number') {
            medication.id = newId;
            this._items.set([...this._items()]);
          }
          this.reload();
        },
        error: error => {
          this._items.set(previous);
          
        }
      })
    );
  }

  /**
   * Save a new medication to the practice's medication list (not to the patient) and return it
   * with its new id. Used by the script editor's "add new medication"; the caller then puts the
   * returned item on the script. Needs Clinical.Medication.Write on the server.
   */
  saveToPracticeList(medication: Medication): Observable<Medication> {
    return this.repo.createMedicationItem(medication).pipe(
      map(id => ({ ...medication, id } as Medication))
    );
  }

  /**
   * Search for medication to add (v2, #333): the practice's medication list, plus the medicine
   * library when the practice has it on. A library pick is saved to the practice list, so a caller
   * without Clinical.Medication.Write passes includeLibrary = false and gets the practice list only.
   */
  search(query: string, includeLibrary: boolean): Observable<MedicationSearchEntry[]> {
    return this.repo.search(query).pipe(
      map(result => includeLibrary ? result : { ...result, libraryProducts: [] }),
      map(result => medicationSearchEntries(result))
    );
  }

  remove(medicationId: number): Observable<unknown> {
    const patient = this.patientStore.current();
    if (!patient) {
      throw new Error('PatientMedicationStore.remove() called with no current patient');
    }
    const previous = this._items();
    this._items.set(previous.filter(m => m.id !== medicationId));
    return this.repo.removeMedicationFromPatient(patient.id, medicationId).pipe(
      tap({
        // The remaining items' overlap flags may have pointed at the removed one, so reload them (#333). The
        // server's push event skips this browser's socket, so it won't do it for us.
        next: () => this.reload(),
        error: error => {
          this._items.set(previous);
          
        }
      })
    );
  }

  private fetch(patientId: number): void {
    this.fetchSub?.unsubscribe();
    this._loading.set(true);
    this._error.set(null);
    // v2: each item carries its overlap flag (#333), worked out on the server for the whole list.
    this.fetchSub = this.repo.loadPatientMedicationWithOverlaps(patientId).subscribe({
      next: medication => {
        const sorted = [...medication].sort((a, b) =>
          a.createdDate > b.createdDate ? 1 : -1
        );
        this._items.set(sorted);
        this._loading.set(false);
      },
      error: error => {
        this._loading.set(false);
        this._error.set('Could not load patient medication');
        
      }
    });
  }
}
